'use client';

import { useMemo } from 'react';
import { Layer } from 'react-map-gl';
import { useMapTheme } from '../../context/map-context';
import { USER_TILES_SOURCE_ID } from './user-tiles-source';
import { usePreferences } from '@streetferret/providers';
import { type SymbolLayout } from 'mapbox-gl';

export interface PeaksLayersProps {
  selectedPeakIds?: string[] | null;
  hoveredPeakIds?: string[] | null;
  /**
   * if provided, only show progress for these cities.
   */
  peakIncludeFilter?: number[];
  /**
   * if provided, exclude these cities.
   */
  peakExcludeFilter?: number[];
  hidden?: boolean;
}

export function PeaksLayers({ hidden }: PeaksLayersProps) {
  const { theme, baseTheme } = useMapTheme();
  const { unit } = usePreferences();
  const visibility = useMemo(() => (hidden ? 'none' : 'visible'), [hidden]);

  // Memoize all layer configuration to prevent unnecessary re-renders
  const layerConfig = useMemo(() => {
    // Use adaptive colors for better dark mode contrast
    const textColor = baseTheme === 'dark' ? '#E6D5AC' : theme.peaks;
    const haloColor = baseTheme === 'dark' ? '#1a1a1a' : '#ffffff';
    const haloWidth = baseTheme === 'dark' ? 3 : 2;

    return {
      layout: {
        visibility,
        'icon-image': 'peak',
        'icon-size': [
          'interpolate',
          ['linear'],
          ['zoom'],
          10,
          0.8,
          14,
          1.2,
          16,
          1.5,
        ],
        'icon-allow-overlap': false,
        'icon-ignore-placement': false,
        'symbol-sort-key': ['-', ['get', 'elevation']],
        'text-field': [
          'concat',
          ['get', 'name'],
          '\n',
          [
            'case',
            ['has', 'elevation'],
            [
              'concat',
              [
                'number-format',
                unit === 'imperial'
                  ? ['round', ['*', ['get', 'elevation'], 3.28084]]
                  : ['get', 'elevation'],
                {},
              ],
              unit === 'imperial' ? ' ft' : ' m',
            ],
            '',
          ],
        ],
        'text-font': ['Americana-Bold'],
        'text-size': [
          'interpolate',
          ['linear'],
          ['zoom'],
          10,
          10,
          14,
          12,
          16,
          14,
        ],
        'text-anchor': 'top',
        'text-offset': [0, 1.2],
        'text-keep-upright': true,
        'text-allow-overlap': false,
        'text-ignore-placement': false,
        'symbol-placement': 'point',
        'text-max-width': 8,
      },
      paint: {
        'icon-color': textColor,
        'icon-halo-color': haloColor,
        'icon-halo-width': haloWidth,
        'text-color': textColor,
        'text-halo-color': haloColor,
        'text-halo-width': haloWidth,
      },
    };
  }, [baseTheme, theme.peaks, unit, visibility]);

  return (
    <>
      <Layer
        id="peaks-icon-layer"
        source={USER_TILES_SOURCE_ID}
        source-layer="peaks"
        type="symbol"
        layout={layerConfig.layout as SymbolLayout}
        paint={layerConfig.paint}
      />
    </>
  );
}
